import "./Scheduler.scss";

import { displayName, formatDateInEN } from "../../../../../commons/util/HallAppUtil";
import { useEffect, useState } from "react";

import AppLoading from "../../../../../commons/AppLoading";
import CompanyAPI from "../../../../company/CompanyAPI";
import FloatingButton from "../../../../../commons/buttons/FloatingButton";
import FloatingVideoButton from "../../../../../commons/buttons/FloatingVideoButton";
import HallAppToast from "../../../../../commons/HallAppToast";
import HeaderMobile from "./HeaderMobile";
import ModalScheduling from "../ModalScheduling";
import MonthViewMobile from "./MonthViewMobile";
import SchedulerConfigAPI from "../../../SchedulerConfigAPI";
import SchedulingAPI from "../../../SchedulingAPI";
import UserService from "../../../../../auth/UserService";
import WeekViewMobile from "./WeekViewMobile";
import WeekViewMobileByEmployee from "./WeekViewByEmployeeMobile";
import { faAdd } from "@fortawesome/free-solid-svg-icons";

const schedulingAPI = new SchedulingAPI();
const userAPI = new UserService();
const companyAPI = new CompanyAPI();
const schedulerConfig = new SchedulerConfigAPI();

// helpers
const isBlank = (v) => v == null || String(v).trim() === "";
const pick = (...vals) => vals.find((v) => !isBlank(v)) ?? "";

function Scheduler() {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState("day"); // 'day'(semana) | 'month' | 'employee'
  const [isLoading, setIsLoading] = useState(true);
  const [prefillDate, setPrefillDate] = useState(null);
  const [prefillStartTime, setPrefillStartTime] = useState("");

  const [events, setEvents] = useState([]);
  const [showModalScheduling, setShowModalScheduling] = useState(false);
  const [eventSelected, setEventSelected] = useState(null);

  const [employees, setEmployees] = useState([]);
  const [optionEmployees, setOptionEmployees] = useState([]);
  const [optionEmployeeSelected, setOptionEmployeeSelected] = useState([]);
  const [closeDiary, setCloseDiary] = useState([]);

  const [employeeSelected, setEmployeeSelected] = useState(null);
  const [userAdmin, setUserAdmin] = useState(false);

  const [filter, setFilter] = useState({
    startDate: formatDateInEN(currentDate),
    endDate: null,
    viewType: viewMode,
    employeeId: userAPI.getUserData().id,
  });

  useEffect(() => {
    advancedSearch();
    getCloseDiary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  useEffect(() => {
    const isAdmin = isUserAdmin();
    getAllEmployee();
    setUserAdmin(isAdmin);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function isUserAdmin() {
    const user = userAPI.getUserData();
    return (
      user.rules?.length !== 0 &&
      (user.rules?.includes("COMPANY") || user.rules?.includes("RECEPTION"))
    );
  }

  /**
   * Normaliza weeksConfig do backend para:
   * weeksConfigMap[WEEK] = {
   *   morning: { start, end },
   *   afternoon: { start, end },
   *   // compat:
   *   startTime, endTime
   * }
   *
   * ⚠️ O backend novo veio com "endTimeTimeMorning" e "endTimeTimeAfternoon".
   * Mantive compat com possíveis nomes antigos também.
   */
  const normalizeWeeksConfig = (weeksConfig = []) => {
    const map = {};

    (weeksConfig || []).forEach((w) => {
      const weekKey = w.week;

      const startMorning = pick(w.startTimeMorning, w.startTimeMorning?.trim?.());
      const endMorning = pick(
        w.endTimeMorning,
        w.endTimeTimeMorning,
        w.endTimeTimeMorning?.trim?.()
      );

      const startAfternoon = pick(w.startTimeAfternoon, w.startTimeAfternoon?.trim?.());
      const endAfternoon = pick(
        w.endTimeAfternoon,
        w.endTimeTimeAfternoon,
        w.endTimeTimeAfternoon?.trim?.()
      );

      const morning = { start: startMorning, end: endMorning };
      const afternoon = { start: startAfternoon, end: endAfternoon };

      // compat (para componentes antigos que usam só startTime/endTime)
      const startTime = pick(morning.start, afternoon.start);
      const endTime = pick(afternoon.end, morning.end);

      map[weekKey] = {
        morning,
        afternoon,
        startTime,
        endTime,
      };
    });

    return map;
  };

  const getAllEmployee = async () => {
    try {
      const companyId = companyAPI.getCompanyManagment().id;
      const userId = userAPI.getUserData().id;

      const response = await schedulerConfig.getEmploees(companyId);

      const employeesNormalized = (response.data || []).map((emp) => {
        const weeksConfigMap = normalizeWeeksConfig(emp.weeksConfig || []);
        return { ...emp, weeksConfigMap };
      });

      setEmployees(employeesNormalized);

      // seleciona o próprio usuário por padrão
      const selected = employeesNormalized.find((e) => e.id === userId) || null;
      setEmployeeSelected(selected);

      const options = employeesNormalized.map((d) => ({
        value: d.id,
        label: displayName(d.name) || d.email,
      }));

      setOptionEmployees(options);
      setOptionEmployeeSelected(options.find((e) => e.value === userId));
    } catch (_) {
      setEmployees([]);
      setOptionEmployees([]);
      setEmployeeSelected(null);
      setOptionEmployeeSelected(null);
    }
  };

  const handleDateChange = (newDate) => {
    setCurrentDate(newDate);
  };

  const getMonthRange = (dateStr) => {
    const [year, month] = dateStr.split("-").map(Number);

    const firstDay = new Date(year, month - 1, 1);
    const lastDay = new Date(year, month, 0);

    const format = (d) =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
        d.getDate()
      ).padStart(2, "0")}`;

    return {
      firstDay: format(firstDay),
      lastDay: format(lastDay),
    };
  };

  const getEmployeeIdForFilter = (prev) =>
    employeeSelected?.id ?? prev?.employeeId ?? userAPI.getUserData().id;

  const handleDayClick = (date) => {
    setCurrentDate(date);

    setFilter((prev) => {
      const dateEN = formatDateInEN(date);
      const employeeId = getEmployeeIdForFilter(prev);

      if (prev.viewType === "month") {
        const dateMonth = getMonthRange(dateEN);
        return {
          ...prev,
          employeeId,
          startDate: dateMonth.firstDay,
          endDate: dateMonth.lastDay,
        };
      }

      return {
        ...prev,
        employeeId,
        startDate: dateEN,
        endDate: null,
      };
    });
  };

  const buildFilterForView = (prev, mode, baseDate) => {
    const employeeId = getEmployeeIdForFilter(prev);
    const dateEN = formatDateInEN(baseDate);

    if (mode === "month") {
      const month = getMonthRange(dateEN);
      return {
        ...prev,
        viewType: "month",
        employeeId,
        startDate: month.firstDay,
        endDate: month.lastDay,
      };
    }

    if (mode === "employee") {
      return {
        ...prev,
        viewType: "employee",
        employeeId,
        startDate: dateEN,
        endDate: null,
      };
    }

    // "day" aqui é sua visão SEMANAL (mantendo como você já usa)
    return {
      ...prev,
      viewType: "day",
      employeeId,
      startDate: dateEN,
      endDate: null,
    };
  };

  const advancedSearch = async () => {
    try {
      setIsLoading(true);
      const resp = await schedulingAPI.advancedSearch(filter);

      const mapped = resp.data?.map((event) => {
        const [year, month, day] = event.schedulingDate.split("-").map(Number);
        const [startHour, startMinute] = event.startTime.split(":").map(Number);
        const [endHour, endMinute] = event.endTime.split(":").map(Number);

        return {
          id: event.id,
          title: event.serviceName,
          client: event.customerName,
          start: new Date(year, month - 1, day, startHour, startMinute),
          end: new Date(year, month - 1, day, endHour, endMinute),
          startTime: event.startTime,
          endTime: event.endTime,
          phone: event.phone,
          customerId: event?.customerId,
          serviceId: event.serviceId,
          employeeId: event.employeeId,
          commandId: event?.commandId,
          value: event?.value,
          commandStatus: event?.commandStatus,
          groupId: event?.groupId,
          promotionId: event?.promotionId ?? null,
          promotionName: event?.promotionName ?? null,
          hasClub: event?.hasClub
        };
      });

      setEvents(mapped || []);
    } catch (_) {
      HallAppToast.error();
    } finally {
      setIsLoading(false);
    }
  };

  const getCloseDiary = async () => {
    try {
      const resp = await schedulingAPI.getCloseDiary(filter);
      setCloseDiary(resp.data || []);
    } catch (_) {
      setCloseDiary([]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="day-view-mobile">
      <HeaderMobile
        currentDate={currentDate}
        onDateChange={handleDateChange}
        isAdminOrReception={true}
        employeeSelected={employeeSelected}
        employees={employees}
        onEmployeeSelect={(emp) => {
          // Header pode mandar {value,label} OU o próprio employee
          const chosenId = emp?.value ?? emp?.id;
          const chosenEmployee = employees.find((e) => e.id === chosenId) || emp;

          setEmployeeSelected(chosenEmployee);

          setFilter((prev) => ({
            ...prev,
            employeeId: chosenId,
          }));
        }}
      />

      {/* Botões para alternar visualização */}
      <div className="day-view-mobile__view-toggle">
        <button
          className={`day-view-mobile__toggle-btn ${viewMode === "day" ? "day-view-mobile__toggle-btn--active" : ""
            }`}
          onClick={() => {
            setViewMode("day");
            setFilter((prev) => buildFilterForView(prev, "day", currentDate));
          }}
        >
          Semana
        </button>

        <button
          className={`day-view-mobile__toggle-btn ${viewMode === "month" ? "day-view-mobile__toggle-btn--active" : ""
            }`}
          onClick={() => {
            setViewMode("month");
            setFilter((prev) => buildFilterForView(prev, "month", currentDate));
          }}
        >
          Mês
        </button>

        <button
          className={`day-view-mobile__toggle-btn ${viewMode === "employee" ? "day-view-mobile__toggle-btn--active" : ""
            }`}
          onClick={() => {
            setViewMode("employee");
            setFilter((prev) => buildFilterForView(prev, "employee", currentDate));
          }}
        >
          Profissional
        </button>
      </div>

      {isLoading || showModalScheduling ? (
        <AppLoading />
      ) : viewMode === "day" ? (
        <WeekViewMobile
          key={`week-${viewMode}-${employeeSelected?.id || "none"}`}
          employees={employees}
          selectedEmployeeId={employeeSelected?.id}
          onlyHoursWorked={localStorage.getItem("hallapp_agenda_only_booked") !== "0"}
          currentDate={currentDate}
          intervalMinutes={Number(localStorage.getItem("hallapp_agenda_step_minutes")) || 60}
          events={events}
          closeDiary={closeDiary}
          onDayClick={handleDayClick}
          onEmptySlotClick={({ date, time }) => {
            // garante que o dia do modal fica alinhado com a tela
            handleDayClick(date);
            setPrefillDate(date);
            setPrefillStartTime(time);
            setEventSelected(null);
            setShowModalScheduling(true);
          }}
          eventClick={(event) => {
            setPrefillDate(null);
            setPrefillStartTime("");
            setEventSelected(event);
            setShowModalScheduling(true);
          }}

        />
      ) : viewMode === "employee" ? (
        <WeekViewMobileByEmployee
          key={`byemp-${viewMode}-${employeeSelected?.id || "none"}`}
          closeDiary={closeDiary}
          employees={employees}
          selectedEmployeeId={employeeSelected?.id}
          onlyHoursWorked={localStorage.getItem("hallapp_agenda_only_booked") !== "0"}
          intervalMinutes={Number(localStorage.getItem("hallapp_agenda_step_minutes")) || 60}
          currentDate={currentDate}
          events={events}
          onEmptySlotClick={({ date, time }) => {
            handleDayClick(date);
            setPrefillDate(date);
            setPrefillStartTime(time);
            setEventSelected(null);
            setShowModalScheduling(true);
          }}
          onDayClick={handleDayClick}
          eventClick={(event) => {
            setPrefillDate(null);
            setPrefillStartTime("");
            setEventSelected(event);
            setShowModalScheduling(true);
          }}
        />
      ) : (
        <MonthViewMobile
          key={`month-${viewMode}`}
          currentDate={currentDate}
          events={events}
          closeDiary={closeDiary}
          onDayClick={handleDayClick}
        />
      )}

      <FloatingVideoButton youtubeUrl={"https://www.youtube.com/embed/TO93b3W4TR8"} />

      <FloatingButton
        icon={faAdd}
        onClick={() => {
          setShowModalScheduling(true);
        }}
      />

      {showModalScheduling && (
        <ModalScheduling
          show={showModalScheduling}
          eventSelected={eventSelected}
          employees={optionEmployees}
          currentEmployee={optionEmployeeSelected}
          initialDate={prefillDate}
          initialStartTime={prefillStartTime}
          call={() => {
            setFilter((prev) => ({ ...prev }));
          }}
          closeShow={() => {
            setShowModalScheduling(false);
            if (eventSelected) setEventSelected(null);
            setPrefillDate(null);
            setPrefillStartTime("");
          }}
        />
      )}
    </div>
  );
}

export default Scheduler;
